// Region / group / individual switchers shared by the Past and Today screens
// (prototype's inline pill-button strips at each drill scope).

import React from 'react';
import { cn } from '@/lib/utils';
import { GROUPS, REGIONS, readiness, wArr, workersInGroup, workersInRegion } from '../../engine/data';
import { Dot } from '../../ui';
import type { Tone } from '../../ui';
import { useDash } from '../../state';

const rdyDot: Tone[] = ['good', 'warn', 'bad'];

const pillBase = 'cursor-pointer rounded-full border font-semibold transition-colors';

/** Region buttons. `compact` is the smaller variant used above the group row. */
export const RegionPicker: React.FC<{ activeId: string | null; compact?: boolean; onPick: (rid: string) => void }> = ({
  activeId,
  compact,
  onPick,
}) => (
  <div className={cn('flex flex-wrap gap-2', compact ? 'mb-2' : 'mb-4')}>
    {Object.entries(REGIONS).map(([rid, r]) => {
      const active = rid === activeId;
      return (
        <button
          key={rid}
          type='button'
          onClick={() => onPick(rid)}
          className={cn(
            pillBase,
            'border-2',
            compact ? 'px-3 py-1 text-[11px]' : 'px-4 py-1.5 text-[13px]',
            !active && 'border-border bg-muted text-muted-foreground hover:bg-accent',
          )}
          // Region colours come from data, so they can't be Tailwind classes.
          style={active ? { borderColor: r.color, background: `${r.color}22`, color: r.color } : undefined}
        >
          {r.name}
        </button>
      );
    })}
  </div>
);

/** Group buttons for the active group's region. */
export const GroupPicker: React.FC<{ regionId: string; activeId: number | null; onPick: (gid: number) => void }> = ({
  regionId,
  activeId,
  onPick,
}) => (
  <div className='mb-4 flex flex-wrap gap-1.5'>
    {REGIONS[regionId].groups.map((gid) => (
      <button
        key={gid}
        type='button'
        onClick={() => onPick(gid)}
        className={cn(
          pillBase,
          'px-3.5 py-1.5 text-xs font-medium',
          gid === activeId
            ? 'border-success/30 bg-success/10 text-success'
            : 'border-border bg-muted text-muted-foreground hover:bg-accent',
        )}
      >
        {GROUPS[gid].name}
      </button>
    ))}
  </div>
);

/** Worker strip for individual scope, sorted by FED, with a readiness dot. */
export const IndividualPicker: React.FC = () => {
  const dash = useDash();
  const pool =
    dash.activeGroup !== null
      ? workersInGroup(dash.activeGroup)
      : dash.activeRegion
        ? workersInRegion(dash.activeRegion)
        : wArr();
  const sorted = [...pool].sort((a, b) => b.fed - a.fed);
  return (
    <div className='mb-3 flex flex-wrap gap-1.5 border-b border-border pb-3'>
      <span className='mr-1 self-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground'>
        Individual
      </span>
      {sorted.map((p) => (
        <button
          key={p.init}
          type='button'
          onClick={() => dash.patch({ activeWorker: p.init })}
          className={cn(
            pillBase,
            'flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium',
            p.init === dash.activeWorker
              ? 'border-success/30 bg-success/10 text-success'
              : 'border-border bg-muted text-muted-foreground hover:bg-accent',
          )}
        >
          <Dot tone={rdyDot[readiness(p)]} className='h-1.5 w-1.5' />
          {p.init} {p.name.split(' ')[0]}
        </button>
      ))}
    </div>
  );
};

/** Region switcher (+ group row at group scope) wired to the dash state. */
export const ScopeSwitchers: React.FC = () => {
  const dash = useDash();
  if (dash.drillScope === 'region') {
    return <RegionPicker activeId={dash.activeRegion} onPick={(rid) => dash.patch({ activeRegion: rid })} />;
  }
  if (dash.drillScope === 'group' && dash.activeGroup !== null) {
    const rid = GROUPS[dash.activeGroup].region;
    return (
      <>
        <RegionPicker
          compact
          activeId={rid}
          onPick={(r) => dash.patch({ activeRegion: r, activeGroup: REGIONS[r].groups[0] })}
        />
        <GroupPicker regionId={rid} activeId={dash.activeGroup} onPick={(gid) => dash.patch({ activeGroup: gid })} />
      </>
    );
  }
  if (dash.drillScope === 'individual') return <IndividualPicker />;
  return null;
};
